import React, { useState } from 'react';

interface AdditionalDoc {
  file: File;
  fileName: string;
  description: string;
}

interface TaxPaymentData {
  w9File: File | null;
  w9FileName: string;
  mcAuthorityFile: File | null;
  mcAuthorityFileName: string;
  dotAuthorityFile: File | null;
  dotAuthorityFileName: string;
  voidedCheckFile: File | null;
  voidedCheckFileName: string;
  paymentMethod: string;
  bankName: string;
  routingNumber: string;
  accountNumber: string;
  confirmAccountNumber: string;
  factoringCompany: string;
  noaFile: File | null;
  noaFileName: string;
  additionalDocs: AdditionalDoc[];
}

interface Step4Props {
  data: TaxPaymentData;
  onNext: () => void;
  onBack: () => void;
  onSave: (data: TaxPaymentData) => void;
}

const Step4TaxPayment: React.FC<Step4Props> = ({ data, onNext, onBack, onSave }) => {
  const [formData, setFormData] = useState<TaxPaymentData>({
    w9File: data.w9File || null,
    w9FileName: data.w9FileName || '',
    mcAuthorityFile: data.mcAuthorityFile || null,
    mcAuthorityFileName: data.mcAuthorityFileName || '',
    dotAuthorityFile: data.dotAuthorityFile || null,
    dotAuthorityFileName: data.dotAuthorityFileName || '',
    voidedCheckFile: data.voidedCheckFile || null,
    voidedCheckFileName: data.voidedCheckFileName || '',
    paymentMethod: data.paymentMethod || 'ach',
    bankName: data.bankName || '',
    routingNumber: data.routingNumber || '',
    accountNumber: data.accountNumber || '',
    confirmAccountNumber: data.confirmAccountNumber || '',
    factoringCompany: data.factoringCompany || '',
    noaFile: data.noaFile || null,
    noaFileName: data.noaFileName || '',
    additionalDocs: data.additionalDocs || [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, field: string) => {
    const file = e.target.files?.[0];
    if (file) {
      const fileNameField = field.replace('File', 'FileName');
      setFormData(prev => ({
        ...prev,
        [field]: file,
        [fileNameField]: file.name,
      }));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAdditionalDoc = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        additionalDocs: [
          ...prev.additionalDocs,
          { file, fileName: file.name, description: '' }
        ]
      }));
    }
    e.target.value = '';
  };

  const updateAdditionalDocDescription = (index: number, description: string) => {
    setFormData(prev => ({
      ...prev,
      additionalDocs: prev.additionalDocs.map((doc, i) =>
        i === index ? { ...doc, description } : doc
      )
    }));
  };

  const removeAdditionalDoc = (index: number) => {
    setFormData(prev => ({
      ...prev,
      additionalDocs: prev.additionalDocs.filter((_, i) => i !== index)
    }));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.w9File && !formData.w9FileName) {
      newErrors.w9File = 'W-9 form is required';
    }

    if (formData.paymentMethod === 'ach') {
      if (!formData.bankName) newErrors.bankName = 'Bank name is required';
      if (!formData.routingNumber) {
        newErrors.routingNumber = 'Routing number is required';
      } else if (formData.routingNumber.length !== 9) {
        newErrors.routingNumber = 'Routing number must be 9 digits';
      }
      if (!formData.accountNumber) {
        newErrors.accountNumber = 'Account number is required';
      }
      if (formData.accountNumber !== formData.confirmAccountNumber) {
        newErrors.confirmAccountNumber = 'Account numbers do not match';
      }
    }

    if (formData.paymentMethod === 'factoring') {
      if (!formData.factoringCompany) {
        newErrors.factoringCompany = 'Factoring company name is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
      onNext();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Tax & Payment Information</h2>
        <p className="text-gray-600 text-sm">
          Upload required tax documents and set up your payment method.
        </p>
      </div>

      {/* Required Documents Section */}
      <div className="space-y-4">
        <h3 className="font-medium text-gray-900">Required Documents</h3>

        {/* W-9 */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">W-9 Form *</h4>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => handleFileChange(e, 'w9File')}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700"
          />
          {formData.w9FileName && (
            <p className="mt-2 text-sm text-green-600">&#10003; {formData.w9FileName}</p>
          )}
          {errors.w9File && <p className="mt-1 text-sm text-red-600">{errors.w9File}</p>}
        </div>

        {/* MC Authority */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2">MC Authority Letter (Optional)</h4>
          <p className="text-sm text-gray-600 mb-2">FMCSA Motor Carrier Authority document</p>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => handleFileChange(e, 'mcAuthorityFile')}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-600 file:text-white hover:file:bg-gray-700"
          />
          {formData.mcAuthorityFileName && (
            <p className="mt-2 text-sm text-green-600">&#10003; {formData.mcAuthorityFileName}</p>
          )}
        </div>

        {/* DOT Authority */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2">DOT Authority Letter (Optional)</h4>
          <p className="text-sm text-gray-600 mb-2">Department of Transportation registration</p>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => handleFileChange(e, 'dotAuthorityFile')}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-600 file:text-white hover:file:bg-gray-700"
          />
          {formData.dotAuthorityFileName && (
            <p className="mt-2 text-sm text-green-600">&#10003; {formData.dotAuthorityFileName}</p>
          )}
        </div>
      </div>

      {/* Payment Method */}
      <div className="space-y-4">
        <h3 className="font-medium text-gray-900">Payment Method</h3>

        <div className="flex gap-4">
          {['ach', 'factoring', 'check'].map((method) => (
            <label key={method} className="flex items-center">
              <input
                type="radio"
                name="paymentMethod"
                value={method}
                checked={formData.paymentMethod === method}
                onChange={handleChange}
                className="mr-2"
              />
              <span className="capitalize">{method === 'ach' ? 'ACH / Direct Deposit' : method}</span>
            </label>
          ))}
        </div>

        {/* ACH Fields */}
        {formData.paymentMethod === 'ach' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name *</label>
              <input
                type="text"
                name="bankName"
                value={formData.bankName}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md ${errors.bankName ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.bankName && <p className="text-sm text-red-600">{errors.bankName}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Routing Number *</label>
                <input
                  type="text"
                  name="routingNumber"
                  value={formData.routingNumber}
                  onChange={handleChange}
                  maxLength={9}
                  placeholder="9 digits"
                  className={`w-full px-3 py-2 border rounded-md ${errors.routingNumber ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.routingNumber && <p className="text-sm text-red-600">{errors.routingNumber}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Number *</label>
                <input
                  type="text"
                  name="accountNumber"
                  value={formData.accountNumber}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md ${errors.accountNumber ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.accountNumber && <p className="text-sm text-red-600">{errors.accountNumber}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Account Number *</label>
              <input
                type="text"
                name="confirmAccountNumber"
                value={formData.confirmAccountNumber}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md ${errors.confirmAccountNumber ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.confirmAccountNumber && <p className="text-sm text-red-600">{errors.confirmAccountNumber}</p>}
            </div>

            {/* Voided Check */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Voided Check (Optional)</label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleFileChange(e, 'voidedCheckFile')}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-green-600 file:text-white hover:file:bg-green-700"
              />
              {formData.voidedCheckFileName && (
                <p className="mt-2 text-sm text-green-600">&#10003; {formData.voidedCheckFileName}</p>
              )}
            </div>
          </div>
        )}

        {/* Factoring Fields */}
        {formData.paymentMethod === 'factoring' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Factoring Company Name *</label>
              <input
                type="text"
                name="factoringCompany"
                value={formData.factoringCompany}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md ${errors.factoringCompany ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.factoringCompany && <p className="text-sm text-red-600">{errors.factoringCompany}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notice of Assignment (NOA)</label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleFileChange(e, 'noaFile')}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-yellow-600 file:text-white hover:file:bg-yellow-700"
              />
              {formData.noaFileName && (
                <p className="mt-2 text-sm text-green-600">&#10003; {formData.noaFileName}</p>
              )}
            </div>
          </div>
        )}

        {/* Check Info */}
        {formData.paymentMethod === 'check' && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">
              Checks will be mailed to the address provided in your company information.
              Please allow 5-7 business days for delivery.
            </p>
          </div>
        )}
      </div>

      {/* Additional Documents */}
      <div className="space-y-4">
        <h3 className="font-medium text-gray-900">Additional Documents (Optional)</h3>
        <p className="text-sm text-gray-600">
          Upload any other documents your broker may require (permits, certifications, etc.)
        </p>

        {formData.additionalDocs.length > 0 && (
          <div className="space-y-3">
            {formData.additionalDocs.map((doc, index) => (
              <div key={index} className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-600">&#10003; {doc.fileName}</p>
                  <input
                    type="text"
                    placeholder="Add description (e.g., TWIC Card, Hazmat Cert)"
                    value={doc.description}
                    onChange={(e) => updateAdditionalDocDescription(index, e.target.value)}
                    className="mt-1 w-full px-2 py-1 text-sm border border-gray-300 rounded"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeAdditionalDoc(index)}
                  className="text-red-500 hover:text-red-700 text-sm"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
          <input
            type="file"
            id="additionalDoc"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleAdditionalDoc}
            className="hidden"
          />
          <label
            htmlFor="additionalDoc"
            className="cursor-pointer text-blue-600 hover:text-blue-700 font-medium"
          >
            + Add Document
          </label>
          <p className="text-xs text-gray-500 mt-1">PDF, JPG, PNG (max 10MB)</p>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <button
          type="button"
          onClick={onBack}
          className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Back
        </button>
        <button
          type="submit"
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Next
        </button>
      </div>
    </form>
  );
};

export default Step4TaxPayment;